import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Plus,
  Search,
  Columns3,
  AlertTriangle,
  Scale as EsitiUdienzaLogoIcon
} from 'lucide-react';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('it-IT');
};

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const getColorPickerValue = (value) => {
  const trimmed = String(value || '').trim();
  return HEX_COLOR_REGEX.test(trimmed) ? trimmed : '#64748b';
};

export default function EsitiUdienza() {
  const columnDefs = [
    { key: 'codice', label: 'Codice', sortable: true },
    { key: 'nome', label: 'Nome', sortable: true },
    { key: 'descrizione', label: 'Descrizione', sortable: true },
    { key: 'colore', label: 'Colore', sortable: true },
    { key: 'attivo', label: 'Attivo', sortable: true },
    { key: 'udienze_totale', label: 'Totale Udienze', sortable: true },
    { key: 'udienze_percentuale', label: '% sul totale', sortable: true }
  ];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('nome');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'nome',
    'descrizione',
    'colore',
    'attivo',
    'udienze_totale',
    'udienze_percentuale'
  ]);
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [codice, setCodice] = useState('');
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [colore, setColore] = useState('');
  const [attivo, setAttivo] = useState(true);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await api.get('/esiti-udienza');
      setRows(res.data || []);
    } catch (error) {
      console.error('Error fetching esiti udienza:', error);
      toast.error('Errore nel caricamento degli esiti udienza');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.codice, row.nome, row.descrizione, row.colore]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];

      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;

      if (
        orderBy === 'id' ||
        orderBy === 'attivo' ||
        orderBy === 'udienze_totale' ||
        orderBy === 'udienze_percentuale'
      ) {
        return order === 'asc' ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      if (orderBy === 'created_at' || orderBy === 'updated_at') {
        const dateA = new Date(valueA).getTime();
        const dateB = new Date(valueB).getTime();
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }

      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredRows, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const displayedColumns = useMemo(
    () => columnDefs.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((prev) => {
      const isVisible = prev.includes(columnKey);
      if (isVisible && prev.length === 1) {
        return prev;
      }
      if (isVisible) {
        return prev.filter((key) => key !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const resetForm = () => {
    setCodice('');
    setNome('');
    setDescrizione('');
    setColore('');
    setAttivo(true);
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setCodice(item.codice || '');
    setNome(item.nome || '');
    setDescrizione(item.descrizione || '');
    setColore(item.colore || '');
    setAttivo(Boolean(item.attivo));
    setDialogOpen(true);
  };

  const openDeleteDialog = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!codice.trim()) {
      toast.error('Il codice è obbligatorio');
      return;
    }
    if (!nome.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }

    const payload = {
      codice: codice.trim().toUpperCase(),
      nome: nome.trim(),
      descrizione: descrizione.trim() || null,
      colore: colore.trim() || null,
      attivo
    };

    try {
      setSaving(true);
      if (editingItem?.id) {
        await api.put(`/esiti-udienza/${editingItem.id}`, payload);
        toast.success('Esito udienza aggiornato');
      } else {
        await api.post('/esiti-udienza', payload);
        toast.success('Esito udienza creato');
      }
      setDialogOpen(false);
      await fetchRows();
    } catch (error) {
      console.error('Error saving esito udienza:', error);
      const message = error.response?.data?.message || 'Errore nel salvataggio dell\'esito udienza';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/esiti-udienza/${deleteTarget.id}`);
      toast.success('Esito udienza eliminato');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchRows();
    } catch (error) {
      console.error('Error deleting esito udienza:', error);
      const message = error.response?.data?.message || 'Errore nell\'eliminazione dell\'esito udienza';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <EsitiUdienzaLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Esiti Udienza
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Definisci l'elenco ufficiale degli esiti udienza (codice, etichetta e colore) usato nelle pratiche U/O per selezione, filtri e badge.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Plus size={18} />} onClick={openCreateDialog}>
          Nuovo esito
        </Button>
      </Box>

      <Menu
        anchorEl={columnsMenuAnchor}
        open={Boolean(columnsMenuAnchor)}
        onClose={() => setColumnsMenuAnchor(null)}
      >
        {columnDefs.map((column) => {
          const checked = visibleColumns.includes(column.key);
          const disableHide = checked && visibleColumns.length === 1;
          return (
            <MenuItem
              key={column.key}
              dense
              onClick={() => handleToggleColumn(column.key)}
              disabled={disableHide}
            >
              <Checkbox size="small" checked={checked} />
              <ListItemText primary={column.label} />
            </MenuItem>
          );
        })}
      </Menu>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Cerca per codice, nome o descrizione..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 420 }}
          />
          <Tooltip title="Colonne">
            <IconButton onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}>
              <Columns3 size={18} />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                {displayedColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={
                      column.key === 'udienze_totale' || column.key === 'udienze_percentuale'
                        ? 'right'
                        : 'left'
                    }
                    sortDirection={column.sortable && orderBy === column.key ? order : false}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.key}
                        direction={orderBy === column.key ? order : 'asc'}
                        onClick={() => handleRequestSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun esito udienza presente
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      {displayedColumns.map((column) => (
                        <TableCell
                          key={`${row.id}-${column.key}`}
                          align={
                            column.key === 'udienze_totale' || column.key === 'udienze_percentuale'
                              ? 'right'
                              : 'left'
                          }
                          sx={column.key === 'nome' ? { fontWeight: 600 } : {}}
                        >
                          {column.key === 'created_at' || column.key === 'updated_at' ? (
                            formatDateTime(row[column.key])
                          ) : column.key === 'udienze_totale' ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(245, 158, 11, 0.35)',
                                backgroundColor: 'rgba(245, 158, 11, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: 0.2 }}
                              >
                                {Number(row.udienze_totale) || 0}
                              </Typography>
                            </Box>
                          ) : column.key === 'udienze_percentuale' ? (
                            (() => {
                              const rawPercent = Number(row.udienze_percentuale);
                              const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                              const clampedPercent = Math.max(0, Math.min(100, safePercent));
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <CircularProgress
                                      variant="determinate"
                                      value={100}
                                      size={36}
                                      thickness={6}
                                      sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                                    />
                                    <CircularProgress
                                      variant="determinate"
                                      value={clampedPercent}
                                      size={36}
                                      thickness={6}
                                      sx={{ position: 'absolute', left: 0 }}
                                      color="warning"
                                    />
                                    <Box
                                      sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}
                                      >
                                        {safePercent.toFixed(0)}%
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })()
                          ) : column.key === 'attivo' ? (
                            <Checkbox checked={Boolean(row.attivo)} disabled />
                          ) : column.key === 'colore' ? (
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                border: '1px solid #cbd5e1',
                                bgcolor: row.colore || '#e2e8f0'
                              }}
                            />
                          ) : (
                            row[column.key] || '-'
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        <Tooltip title="Modifica">
                          <IconButton color="primary" onClick={() => openEditDialog(row)}>
                            <EditIcon size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Elimina">
                          <IconButton color="error" onClick={() => openDeleteDialog(row)}>
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Modifica esito udienza' : 'Nuovo esito udienza'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Codice"
                value={codice}
                onChange={(e) => setCodice(e.target.value.toUpperCase())}
                fullWidth
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrizione"
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Colore"
                type="color"
                value={getColorPickerValue(colore)}
                onChange={(e) => setColore(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'Seleziona colore' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Checkbox checked={attivo} onChange={(e) => setAttivo(e.target.checked)} />
                <Typography variant="body2">Attivo</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={(_, reason) => {
          if (deleteLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setDeleteDialogOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                bgcolor: 'error.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AlertTriangle size={16} color="#b71d18" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Conferma eliminazione
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Stai per eliminare definitivamente l'esito udienza:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {deleteTarget?.nome || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Codice: {deleteTarget?.codice || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Questa operazione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            No, annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Sì, elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
